import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { consultPatientActions } from 'src/app/core/component/consult-patient/store/consult-patient.action';
import { RestService } from 'src/app/core/service/rest.service';
import { Utils } from 'src/app/core/utils/khuyana.utils';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { OdontoDiagnosticViewModel } from 'src/app/model/odonto/diagnostic.view';
import { OdontoOralHygieneModel } from 'src/app/model/odonto/oral-hygiene';
import { OdontoTherapyViewModel } from 'src/app/model/odonto/therapy.view';
import { OdontoWrapperDto } from 'src/app/model/odonto/wrapper.dto';
import { historyActions } from 'src/app/store/history/history.action';
import { environment } from 'src/environments/environment';
import { commonTurnActions } from '../../store/common/turn/turn.action';
import { PatientService } from '../common/patient/patient.service';
import { PersonService } from '../common/person/person.service';
import { CommonQuestionViewModel } from 'src/app/model/common/question.view';

@Injectable({
  providedIn: 'root',
})
export class OdontoMainService extends BaseService {
  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected patientService: PatientService,
    protected personService: PersonService,
    protected http: HttpClient
  ) {
    super();
  }

  getPatientById = this.patientService.getPatientById;

  getPersonById = this.personService.getPersonById;

  readTurnStatusStore = (): Observable<AdminCatalogModel[]> =>
    this.store.select('admin', 'catalog', 'subCatalogs', 'turn_status');

  getTurn = (id: number): Observable<CommonTurnViewModel> =>
    this.rest
      .get({
        url: `${environment.endpoints.common}/turn/{id}`,
        urlParams: { id },
      })
      .pipe(
        tap((turn) =>
          this.store.dispatch(
            historyActions.setPersonIdAction({ personId: turn.person! })
          )
        )
      );

  getOldTurns = (
    patient: number,
    turn: number
  ): Observable<CommonTurnViewModel[]> =>
    this.rest.get({
      url: `${environment.endpoints.common}/turn/not/{turn}/patient/{patient}`,
      urlParams: { patient, turn },
    });

  getPatientView = (id: number): Observable<CommonPatientViewModel> =>
    this.rest
      .get({
        url: `${environment.endpoints.common}/patient/{id}/view`,
        urlParams: { id },
      })
      .pipe(
        map((patient) => {
          if (patient.dateBorn) {
            patient.dateBorn = new Date(patient.dateBorn);
          }
          patient.ageLabel = Utils.transformAge(patient.age);
          return patient;
        })
      );

  getOdontologyInformation = (
    patient?: number,
    turn?: number,
    specialty?: string
  ): Observable<OdontoWrapperDto> => {
    const queries = [
      this.rest.get({
        url: `${environment.endpoints.odonto}/detail/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/header/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/stomatognathic/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/oral-health/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/diagnostic-plan/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/diagnostic/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/therapy/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/history/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/odontogram/{patient}`,
        urlParams: { patient },
      }),
      this.rest.get({
        url: `${environment.endpoints.odonto}/odontogram/symbology`,
      }),
      this.rest.get({
        url: `${environment.endpoints.commonParametrization}/question/{specialty}/{patient}`,
        urlParams: { specialty, patient },
      }),
    ];

    return forkJoin(queries).pipe(
      map((results) => {
        const allTherapies: OdontoTherapyViewModel[] = results[6];

        const allQuestions: CommonQuestionViewModel[] = results[10];
        const topQuestions = allQuestions.filter(
          (question: any) => question.showOnTop
        );
        const bottomQuestions = allQuestions.filter(
          (question: any) => !question.showOnTop
        );

        const wrapperDto: OdontoWrapperDto = {
          detail: results[0],
          header: results[1],
          stomatognathicView: results[2],
          oralHealth: results[3],
          diagnosticPlanView: results[4],
          diagnosticView: results[5].map((item: OdontoDiagnosticViewModel) => ({
            ...item,
            cieHelp: {
              code: item.code,
              description: item.description,
              displayName: item.displayName,
            },
          })),
          historyView: results[7],
          odontogramView: results[8],
          symbology: results[9],
          topQuestions,
          bottomQuestions,
        };

        wrapperDto.therapy = allTherapies.find(
          (therapy) => therapy.turn === turn
        ) || { turn };
        wrapperDto.therapies = allTherapies
          .filter((therapy) => therapy.turn !== turn)
          .map((therapy, idx) => ({
            ...therapy,
            idx: idx + 1,
          }))
          .sort((a, b) => b.turn! - a.turn!);

        return wrapperDto;
      })
    );
  };

  getOralHygiene = (
    oralHealth: string | number = ''
  ): Observable<OdontoOralHygieneModel[]> =>
    this.rest
      .get({
        url: `${environment.endpoints.odonto}/oral-hygiene/{oralHealth}`,
        urlParams: { oralHealth },
      })
      .pipe(
        map((item) => [
          { ...this.buildParts(item, '1', '16', '17', '55') },
          { ...this.buildParts(item, '2', '11', '21', '51') },
          { ...this.buildParts(item, '3', '26', '27', '65') },
          { ...this.buildParts(item, '4', '36', '37', '75') },
          { ...this.buildParts(item, '5', '31', '41', '71') },
          { ...this.buildParts(item, '6', '46', '47', '85') },
        ])
      );

  moveTurnStatus = (id?: number, newStatus?: string) =>
    this.rest
      .patch<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn/{id}/toConsult/{newStatus}`,
        urlParams: { id, newStatus },
      })
      .pipe(
        tap((turn) =>
          this.store.dispatch(commonTurnActions.patchResultAction(turn))
        )
      );

  save = (body: OdontoWrapperDto, patient?: number) => {
    const odontoWrapper: OdontoWrapperDto = {
      ...body,
    };
    delete odontoWrapper.historyView;
    delete odontoWrapper.stomatognathicView;
    delete odontoWrapper.odontogramView;
    delete odontoWrapper.oralHygieneView;
    delete odontoWrapper.diagnosticPlanView;
    delete odontoWrapper.diagnosticView;
    delete odontoWrapper.symbology;

    return this.rest.post<boolean>({
      url: `${environment.endpoints.odonto}/{patient}`,
      body: odontoWrapper,
      urlParams: { patient },
    });
  };

  putFiles = (odontogram: File, oralHealth: File, patientHistory?: string) => {
    const odontogramFormData = new FormData();
    odontogramFormData.append('odontogram', odontogram);
    const oralHealthFormData = new FormData();
    oralHealthFormData.append('oralHealth', oralHealth);

    const queries = [
      this.rest.put<boolean>({
        url: `${environment.endpoints.odonto}/odontogram/{patientHistory}/png`,
        body: odontogramFormData,
        urlParams: { patientHistory },
      }),
      this.rest.put<boolean>({
        url: `${environment.endpoints.odonto}/oral-health/{patientHistory}/png`,
        body: oralHealthFormData,
        urlParams: { patientHistory },
      }),
    ];

    return forkJoin(queries);
  };

  generateCertificate = (
    patientHistory?: string,
    turn?: number
  ): Observable<SafeResourceUrl> =>
    this.rest
      .get({
        url: `${environment.endpoints.report}/odontology-history/{patientHistory}/{turn}`,
        urlParams: { patientHistory, turn },
        header: {
          responseType: 'BLOB' as 'json',
        },
      })
      .pipe(
        map(this.rest.mapResponseAsPdf),
        tap((file) =>
          this.store.dispatch(
            consultPatientActions.setHistoryPdfAction({ file })
          )
        )
      );

  private buildParts = (
    hygiene: OdontoOralHygieneModel[],
    partGroup: string,
    part1: string,
    part2: string,
    part3: string
  ) => ({
    partGroup,
    part1,
    part2,
    part3,
    ...hygiene.find((item) => item.partGroup === partGroup),
  });
}
